import type { FC } from 'react';

import classNames from 'classnames';
import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';

import type {
  NavigationItem,
  NavigationItemsProps,
} from './NavigationItems.interface';

export const NavigationItems: FC<NavigationItemsProps> = ({
  navigationItems,
  onClose,
  notifications,
}) => {
  const matchProfileNotifications = useMemo(() => {
    return notifications?.filter(
      (notification) =>
        notification.cta_type === 'check_out_matches' &&
        notification.content.includes(
          'Looks like someone is interested in meeting you.',
        ),
    );
  }, [notifications]);

  const navigationItemClassName = (isActive: boolean) =>
    classNames(
      isActive ? 'bg-forest-400' : '',
      'text-beige-500 block px-3 py-2 rounded-md text-base font-medium sm:text-sm hover:bg-forest-400',
    );

  return (
    <div className="block space-y-1 sm:flex sm:space-y-0 sm:space-x-4">
      {navigationItems?.length
        ? navigationItems.map(({ label, href }: NavigationItem) => {
            const isMatchesRoute = label === 'Matches';

            if (isMatchesRoute) {
              return (
                <MatchProfileNavigationItem
                  key={label}
                  label={label}
                  href={href}
                  notificationCount={matchProfileNotifications?.length ?? 0}
                  className={navigationItemClassName}
                  onClick={() => onClose && onClose()}
                />
              );
            }

            return (
              <NavLink
                key={label}
                to={href}
                className={({ isActive }) => navigationItemClassName(isActive)}
                onClick={() => onClose && onClose()}
              >
                {label}
              </NavLink>
            );
          })
        : null}
    </div>
  );
};

interface MatchProfileNavigationItemProps extends NavigationItem {
  notificationCount: number;
  className: (isActive: boolean) => string;
  onClick: () => void;
}

const MatchProfileNavigationItem: FC<MatchProfileNavigationItemProps> = ({
  label,
  href,
  notificationCount,
  className,
  onClick,
}) => {
  return (
    <NavLink
      to={href}
      className={({ isActive }) => className(isActive)}
      onClick={onClick}
    >
      <div className="flex relative items-center">
        {label}
        {notificationCount > 0 && (
          <span className="flex absolute -top-2 -right-6 justify-center items-center px-1 w-6 h-5 text-xs text-white bg-red-500 rounded-full">
            {notificationCount}
          </span>
        )}
      </div>
    </NavLink>
  );
};
